import React, { useState, useEffect } from 'react';
import { api } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Title,
  Card,
  Table,
  Button,
  Group,
  Text,
  Badge,
  Modal,
  TextInput,
  PasswordInput,
  Stack,
  Loader,
  Center,
  ActionIcon,
  Tooltip,
  Checkbox,
  Alert
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconPlus,
  IconPencil,
  IconTrash,
  IconUsers,
  IconAlertCircle,
  IconKey
} from '@tabler/icons-react';

function Utenti() {
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUtente, setEditingUtente] = useState(null);
  const [form, setForm] = useState({
    nome: '', cognome: '', email: '', password: '', attivo: true
  });
  const [error, setError] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user?.email !== 'l.simonelli@me.com') {
      navigate('/');
      return;
    }
    loadUtenti();
  }, [user, navigate]);

  const loadUtenti = async () => {
    try {
      const res = await api.get('/utenti');
      setUtenti(res.data);
    } catch (err) {
      notifications.show({ title: 'Errore', message: 'Impossibile caricare gli utenti', color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingUtente) {
        const data = {
          nome: form.nome,
          cognome: form.cognome,
          email: form.email,
          attivo: form.attivo
        };
        if (form.password) {
          data.password = form.password;
        }
        await api.put(`/utenti/${editingUtente.id}`, data);
        notifications.show({ title: 'Successo', message: 'Utente aggiornato', color: 'green' });
      } else {
        await api.post('/utenti', {
          nome: form.nome,
          cognome: form.cognome,
          email: form.email,
          password: form.password,
          ruolo: 'operatore'
        });
        notifications.show({ title: 'Successo', message: 'Utente creato', color: 'green' });
      }
      setShowModal(false);
      resetForm();
      loadUtenti();
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleEdit = (utente) => {
    setEditingUtente(utente);
    setForm({
      nome: utente.nome || '',
      cognome: utente.cognome || '',
      email: utente.email || '',
      password: '',
      attivo: utente.attivo !== undefined ? utente.attivo : true
    });
    setError('');
    setShowModal(true);
  };

  const handleToggleAttivo = async (utente) => {
    if (utente.email === 'l.simonelli@me.com') {
      notifications.show({ title: 'Attenzione', message: 'Non puoi disattivare l\'amministratore principale', color: 'yellow' });
      return;
    }
    try {
      await api.put(`/utenti/${utente.id}`, {
        ...utente,
        attivo: !utente.attivo
      });
      notifications.show({ title: 'Successo', message: 'Stato utente aggiornato', color: 'green' });
      loadUtenti();
    } catch (err) {
      notifications.show({ title: 'Errore', message: 'Errore nell\'aggiornamento', color: 'red' });
    }
  };

  const resetForm = () => {
    setEditingUtente(null);
    setForm({ nome: '', cognome: '', email: '', password: '', attivo: true });
    setError('');
  };

  const handleDelete = async (utente) => {
    if (utente.email === 'l.simonelli@me.com') {
      notifications.show({ title: 'Attenzione', message: "Non puoi eliminare l'amministratore principale", color: 'yellow' });
      return;
    }
    if (!window.confirm(`Eliminare l'utente ${utente.nome} ${utente.cognome}?`)) return;
    try {
      await api.delete(`/utenti/${utente.id}`);
      notifications.show({ title: 'Successo', message: 'Utente eliminato', color: 'green' });
      loadUtenti();
    } catch (err) {
      notifications.show({ title: 'Errore', message: err.response?.data?.detail || "Errore nell'eliminazione", color: 'red' });
    }
  };

  const handleResetPassword = (utente) => {
    setPasswordTarget(utente);
    setNewPassword('');
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 4) {
      notifications.show({ title: 'Errore', message: 'La password deve avere almeno 4 caratteri', color: 'red' });
      return;
    }
    try {
      await api.put(`/utenti/${passwordTarget.id}`, {
        nome: passwordTarget.nome,
        cognome: passwordTarget.cognome,
        email: passwordTarget.email,
        attivo: passwordTarget.attivo,
        password: newPassword
      });
      notifications.show({ title: 'Successo', message: `Password di ${passwordTarget.nome} ${passwordTarget.cognome} aggiornata`, color: 'green' });
      setShowPasswordModal(false);
      setPasswordTarget(null);
      setNewPassword('');
    } catch (err) {
      notifications.show({ title: 'Errore', message: err.response?.data?.detail || 'Errore nel reset password', color: 'red' });
    }
  };

  if (user?.email !== 'l.simonelli@me.com') {
    return null;
  }

  if (loading) {
    return <Center h={400}><Loader size="lg" color="teal" /></Center>;
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Group>
          <IconUsers size={28} color="rgb(32, 83, 87)" />
          <Title order={2}>Gestione Utenti</Title>
        </Group>
        <Button leftSection={<IconPlus size={18} />} onClick={() => { resetForm(); setShowModal(true); }}>
          Nuovo Utente
        </Button>
      </Group>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Table.ScrollContainer minWidth={700}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Nome</Table.Th>
                <Table.Th>Cognome</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Ruolo</Table.Th>
                <Table.Th>Stato</Table.Th>
                <Table.Th>Azioni</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {utenti.map(u => (
                <Table.Tr key={u.id}>
                  <Table.Td>
                    <Text fw={600}>{u.nome}</Text>
                  </Table.Td>
                  <Table.Td>{u.cognome}</Table.Td>
                  <Table.Td>{u.email}</Table.Td>
                  <Table.Td>
                    <Badge color={u.ruolo === 'admin' ? 'teal' : 'gray'} variant="light">
                      {u.ruolo?.toUpperCase()}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge 
                      color={u.attivo ? 'green' : 'red'} 
                      variant="light"
                      style={{ cursor: u.email === 'l.simonelli@me.com' ? 'not-allowed' : 'pointer' }}
                      onClick={() => u.email !== 'l.simonelli@me.com' && handleToggleAttivo(u)}
                    >
                      {u.attivo ? 'ATTIVO' : 'DISATTIVATO'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Tooltip label="Modifica">
                        <ActionIcon variant="subtle" color="blue" onClick={() => handleEdit(u)}>
                          <IconPencil size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Reset Password">
                        <ActionIcon variant="subtle" color="orange" onClick={() => handleResetPassword(u)}>
                          <IconKey size={16} />
                        </ActionIcon>
                      </Tooltip>
                      {u.email !== 'l.simonelli@me.com' && (
                        <Tooltip label="Elimina">
                          <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(u)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
              {utenti.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={6}>
                    <Text ta="center" c="dimmed" py="xl">Nessun utente trovato</Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      {/* Modal */}
      <Modal
        opened={showModal}
        onClose={() => setShowModal(false)}
        title={editingUtente ? 'Modifica Utente' : 'Nuovo Utente'}
        size="md"
      >
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                {error}
              </Alert>
            )}
            <TextInput
              label="Nome"
              required
              value={form.nome}
              onChange={(e) => setForm({...form, nome: e.target.value})}
            />
            <TextInput
              label="Cognome"
              required
              value={form.cognome}
              onChange={(e) => setForm({...form, cognome: e.target.value})}
            />
            <TextInput
              label="Email"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({...form, email: e.target.value})}
            />
            <PasswordInput
              label={editingUtente ? 'Nuova Password (lascia vuoto per non modificare)' : 'Password'}
              required={!editingUtente}
              value={form.password}
              onChange={(e) => setForm({...form, password: e.target.value})}
            />
            {editingUtente && (
              <Checkbox
                label="Utente attivo"
                checked={form.attivo}
                onChange={(e) => setForm({...form, attivo: e.target.checked})}
              />
            )}
            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={() => setShowModal(false)}>Annulla</Button>
              <Button type="submit">Salva</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Modal Reset Password */}
      <Modal
        opened={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        title={`Reset Password - ${passwordTarget?.nome} ${passwordTarget?.cognome}`}
        size="sm"
      >
        <form onSubmit={handlePasswordSubmit}>
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Inserisci la nuova password per {passwordTarget?.email}
            </Text>
            <PasswordInput
              label="Nuova Password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Inserisci nuova password"
            />
            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={() => setShowPasswordModal(false)}>Annulla</Button>
              <Button type="submit" color="orange">Cambia Password</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}

export default Utenti;
